import React, { useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'
import { useIsMobile } from '../lib/useIsMobile'

// ─── Button ───────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'wa' | 'danger'

const btnBase: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 14,
  borderRadius: 8,
  height: 40,
  padding: '0 16px',
  cursor: 'pointer',
  border: '1px solid transparent',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  transition: 'all 150ms var(--ease-out)',
  whiteSpace: 'nowrap',
}

const btnVariants: Record<ButtonVariant, React.CSSProperties> = {
  primary:   { background: 'var(--orange-500)', color: '#fff', borderColor: 'var(--orange-500)' },
  secondary: { background: '#fff', color: 'var(--fg-1)', borderColor: 'var(--border-strong)' },
  ghost:     { background: 'transparent', color: 'var(--fg-1)' },
  wa:        { background: '#25D366', color: '#fff', borderColor: '#25D366' },
  danger:    { background: '#DC2626', color: '#fff', borderColor: '#DC2626' },
}

interface ButtonProps {
  variant?: ButtonVariant
  icon?: string
  children?: React.ReactNode
  onClick?: () => void
  style?: React.CSSProperties
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
}

export function Button({ variant = 'secondary', icon, children, onClick, style = {}, disabled, type = 'button' }: ButtonProps) {
  const v = btnVariants[variant]
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...btnBase, ...v, ...(disabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}), ...style }}
    >
      {icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────

interface CardProps {
  children: React.ReactNode
  style?: React.CSSProperties
  elevated?: boolean
  onClick?: () => void
}

export function Card({ children, style = {}, elevated = false, onClick }: CardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        border: elevated ? 'none' : '1px solid var(--border-subtle)',
        boxShadow: elevated ? 'var(--shadow-md)' : 'none',
        borderRadius: 12,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

type BadgeStyle = { bg: string; fg: string; dot?: string }

const BADGE_STYLES: Record<string, BadgeStyle> = {
  // Job card statuses
  INTAKE:           { bg: '#F4F4F5', fg: '#374151', dot: '#6B7280' },
  DIAGNOSING:       { bg: '#F0F4FF', fg: '#3730A3', dot: '#6366F1' },
  QUOTED:           { bg: '#FAF5FF', fg: '#6D28D9', dot: '#8B5CF6' },
  APPROVED:         { bg: '#F0FDF4', fg: '#15803D', dot: '#22C55E' },
  'IN PROGRESS':    { bg: '#EFF8FF', fg: '#175CD3', dot: '#2E90FA' },
  'PARTS ORDERED':  { bg: '#FFF7ED', fg: '#C2410C', dot: '#FB923C' },
  READY:            { bg: '#FFFAEB', fg: '#B54708', dot: '#F79009' },
  COLLECTED:        { bg: '#ECFDF3', fg: '#027A48', dot: '#12B76A' },
  INVOICED:         { bg: '#ECFDF3', fg: '#027A48', dot: '#12B76A' },
  COMPLETE:         { bg: '#ECFDF3', fg: '#027A48', dot: '#12B76A' },
  CANCELLED:        { bg: '#F4F4F5', fg: '#9CA3AF', dot: '#D1D5DB' },
  // Invoice / payment statuses
  DRAFT:            { bg: '#F4F4F5', fg: '#374151', dot: '#9CA3AF' },
  SENT:             { bg: '#EFF8FF', fg: '#175CD3', dot: '#2E90FA' },
  PAID:             { bg: '#ECFDF3', fg: '#027A48', dot: '#12B76A' },
  'PARTIALLY PAID': { bg: '#FFFAEB', fg: '#B54708', dot: '#F79009' },
  UNPAID:           { bg: '#F4F4F5', fg: '#374151', dot: '#9CA3AF' },
  OVERDUE:          { bg: '#FEF3F2', fg: '#B42318', dot: '#F04438' },
  // Tenant subscription statuses
  TRIAL:            { bg: '#EFF8FF', fg: '#175CD3', dot: '#2E90FA' },
  'PAST DUE':       { bg: '#FFFAEB', fg: '#B54708', dot: '#F79009' },
  PAUSED:           { bg: '#F4F4F5', fg: '#374151', dot: '#9CA3AF' },
  SUSPENDED:        { bg: '#FEF3F2', fg: '#B42318', dot: '#F04438' },
  // Quote statuses
  REJECTED:         { bg: '#FEF3F2', fg: '#B42318', dot: '#F04438' },
  EXPIRED:          { bg: '#F4F4F5', fg: '#9CA3AF', dot: '#D1D5DB' },
  // Inspection
  PASS:             { bg: '#ECFDF3', fg: '#027A48' },
  ADVISORY:         { bg: '#FFFAEB', fg: '#B54708' },
  FAIL:             { bg: '#FEF3F2', fg: '#B42318' },
  // Purchase order statuses
  'PARTIALLY RECEIVED': { bg: '#FFF7ED', fg: '#C2410C', dot: '#FB923C' },
  RECEIVED:         { bg: '#ECFDF3', fg: '#027A48', dot: '#12B76A' },
  // Vendor statuses
  ACTIVE:           { bg: '#ECFDF3', fg: '#027A48', dot: '#12B76A' },
  INACTIVE:         { bg: '#F4F4F5', fg: '#9CA3AF', dot: '#D1D5DB' },
  // Marketing lead statuses
  NEW:              { bg: '#EFF8FF', fg: '#175CD3', dot: '#2E90FA' },
  CONTACTED:        { bg: '#FFF7ED', fg: '#C2410C', dot: '#FB923C' },
  QUALIFIED:        { bg: '#ECFDF3', fg: '#027A48', dot: '#12B76A' },
  CLOSED:           { bg: '#F4F4F5', fg: '#374151', dot: '#9CA3AF' },
  LOST:             { bg: '#FEF3F2', fg: '#B42318', dot: '#F04438' },
}

export function StatusBadge({ status }: { status: string }) {
  const s = BADGE_STYLES[status] || BADGE_STYLES.INTAKE
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 9px', borderRadius: 999,
      background: s.bg, color: s.fg,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
      fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap',
    }}>
      {s.dot && <span style={{ width: 6, height: 6, borderRadius: 999, background: s.dot, flexShrink: 0 }} />}
      {status}
    </span>
  )
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

interface AvatarProps {
  name: string
  size?: number
  color?: string
}

export function Avatar({ name, size = 32, color = 'var(--navy-700)' }: AvatarProps) {
  const initials = name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()
  return (
    <div style={{
      width: size, height: size, borderRadius: 999,
      background: color, color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 600,
      fontFamily: 'var(--font-sans)', flexShrink: 0,
    }}>
      {initials}
    </div>
  )
}

// ─── Money ────────────────────────────────────────────────────────────────────

type MoneySize = 'sm' | 'base' | 'lg' | 'xl' | 'display'

const moneySizes: Record<MoneySize, number> = { sm: 13, base: 14, lg: 18, xl: 24, display: 36 }

interface MoneyProps {
  value: number
  currency?: string
  size?: MoneySize
  style?: React.CSSProperties
}

export function Money({ value, currency = 'R', size = 'base', style = {} }: MoneyProps) {
  const formatted = Math.abs(value)
    .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .replace(/,/g, '\u00A0')
  return (
    <span className="money" style={{
      fontFamily: 'var(--font-mono)',
      fontVariantNumeric: 'tabular-nums',
      fontSize: moneySizes[size],
      whiteSpace: 'nowrap',
      ...style,
    }}>
      {currency}&nbsp;{value < 0 ? '-' : ''}{formatted}
    </span>
  )
}

// ─── Reg ──────────────────────────────────────────────────────────────────────

type RegSize = 'sm' | 'md' | 'lg'

const regSizes: Record<RegSize, { fs: number; pad: string }> = {
  sm: { fs: 12, pad: '3px 6px' },
  md: { fs: 13, pad: '4px 8px' },
  lg: { fs: 15, pad: '6px 10px' },
}

export function Reg({ value, size = 'md' }: { value: string; size?: RegSize }) {
  const s = regSizes[size]
  return (
    <span style={{
      fontFamily: 'var(--font-mono)',
      fontWeight: 500,
      fontSize: s.fs,
      padding: s.pad,
      background: 'var(--neutral-100)',
      borderRadius: 6,
      letterSpacing: '0.04em',
      color: 'var(--fg-1)',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      {value}
    </span>
  )
}

// ─── PageHeader ───────────────────────────────────────────────────────────────

interface PageHeaderProps {
  title: string
  subtitle?: string
  actions?: React.ReactNode
}

export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  const isMobile = useIsMobile()
  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'flex-end',
      justifyContent: 'space-between',
      marginBottom: isMobile ? 16 : 24,
      gap: isMobile ? 10 : 16,
    }}>
      <div>
        <h1 style={{
          fontSize: isMobile ? 22 : 30, fontFamily: 'var(--font-display)',
          fontWeight: 600, letterSpacing: '-0.02em',
          color: 'var(--fg-1)', margin: 0,
        }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 3 }}>{subtitle}</div>}
      </div>
      {actions && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', width: isMobile ? '100%' : undefined }}>
          {actions}
        </div>
      )}
    </div>
  )
}

// ─── SearchableSelect ─────────────────────────────────────────────────────────

export interface SelectOption {
  value: string
  label: string
}

interface SearchableSelectProps {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  style?: React.CSSProperties
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  style = {},
}: SearchableSelectProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedLabel = options.find(o => o.value === value)?.label ?? ''
  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setQuery(e.target.value)
    setOpen(true)
  }

  function handleFocus() {
    setQuery('')
    setOpen(true)
  }

  function handleSelect(opt: SelectOption) {
    onChange(opt.value)
    setOpen(false)
    setQuery('')
    inputRef.current?.blur()
  }

  const inputBase: React.CSSProperties = {
    width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 8,
    border: '1px solid var(--border-strong)', fontFamily: 'var(--font-sans)',
    background: disabled ? 'var(--bg-2)' : '#fff', color: 'var(--fg-1)',
    boxSizing: 'border-box', outline: 'none',
    cursor: disabled ? 'default' : 'text',
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <input
        ref={inputRef}
        value={open ? query : selectedLabel}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        style={inputBase}
        autoComplete="off"
      />
      {open && !disabled && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid var(--border-strong)',
          borderRadius: 8, boxShadow: 'var(--shadow-md)',
          maxHeight: 220, overflowY: 'auto',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--fg-3)' }}>No results</div>
          ) : filtered.map(opt => (
            <button
              key={opt.value || '__empty__'}
              type="button"
              onMouseDown={e => { e.preventDefault(); handleSelect(opt) }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '9px 12px', fontSize: 14, cursor: 'pointer',
                background: opt.value === value ? 'var(--navy-50)' : 'transparent',
                color: opt.value === value ? 'var(--navy-700)' : opt.label ? 'var(--fg-1)' : 'var(--fg-3)',
                border: 'none', fontFamily: 'var(--font-sans)',
                fontWeight: opt.value === value ? 600 : 400,
              }}
            >
              {opt.label || placeholder}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
